<template>
  <v-dialog :value="value" width="1400" persistent>
    <v-card :loading="loading.postValueSet">
      <AdCardTitle dialog>
        {{ $t('new-reference') }}
      </AdCardTitle>

      <v-card-text>
        <v-form ref="form" v-model="modal.formIsValid">
          <!-- ValueSet search and selection -->
          <v-row dense class="mb-2">
            <v-col>
              <v-data-table
                v-model="modal.inputs.selectedValueSet"
                :headers="headers"
                :items="filteredFetchedValueSets"
                item-key="uuid"
                :loading="loading.getValueSets"
                show-select
                single-select
                :items-per-page="5"
                :hide-default-footer="filteredFetchedValueSets.length <= 5"
                :footer-props="{ 'items-per-page-options': [5] }"
                @click:row="selectItem"
              >
                <!-- Search and filter -->
                <template #top>
                  <v-row class="my-2">
                    <v-spacer />
                    <!-- Object search -->
                    <v-col cols="6">
                      <v-text-field
                        ref="searchInput"
                        v-model.trim="modal.search"
                        :label="$t('search')"
                        @click:append="fetchValueSets"
                        @keydown.enter.prevent="fetchValueSets"
                        append-icon="mdi-magnify"
                        single-line
                        dense
                        solo
                        hide-details
                      />
                    </v-col>
                    <!-- Filter for project idents -->
                    <v-col cols="auto" class="d-flex align-center">
                      <v-menu
                        :close-on-content-click="false"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            :class="{'green': hasActiveProjectFilters}"
                            :color="hasActiveProjectFilters ? 'white' : 'light-green darken-2'"
                            icon
                            outlined
                            small
                            :disabled="!modal.projectFilters.length"
                          >
                            <v-icon small>mdi-filter</v-icon>
                          </v-btn>
                        </template>
                        <v-list v-if="modal.projectFilters.length">
                          <v-list-item-group>
                            <v-list-item
                              v-for="(filter, key) of modal.projectFilters"
                              :key="key"
                              @click="filter.active = !filter.active"
                            >
                              <v-list-item-action>
                                <v-checkbox
                                  :input-value="filter.active"
                                  color="primary"
                                />
                              </v-list-item-action>

                              <v-list-item-content>
                                <v-list-item-title>{{ filter.name }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list-item-group>
                        </v-list>
                      </v-menu>
                    </v-col>
                  </v-row>
                </template>
                <!-- Select radio button -->
                <template #[`item.data-table-select`]="{isSelected, select, item }">
                  <v-radio-group :value="isSelected ? item.uuid : null">
                    <v-radio :value="item.uuid" @input="select(!isSelected)"></v-radio>
                  </v-radio-group>
                </template>
                <!-- Item fields -->
                <template v-slot:[`item.displayName`]="{ item }">
                  <div class="d-flex">
                    <AdStatusDot
                      :status="item.statusCode"
                      icon="mdi-heart"
                      larger
                      tooltip
                      class="mr-2"
                    />
                    <div>
                      {{ $filters.getMatchingTranslation({ value: item.displayName }) }}
                      <span v-if="item.versionLabel && item.versionLabel !== ''">({{ item.versionLabel }})</span>
                    </div>
                  </div>
                </template>
                <template v-slot:[`item.effectiveDate`]="{ item }">
                  {{ $filters.formatDateShort(item.effectiveDate) }}
                </template>
                <template v-slot:[`item.project`]="{ item }">
                  {{ item.ident || item.project }}
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <AdButton type="cancel" @click="closeModal" :disabled="loading.postValueSet"/>
        <AdButton
          type="add"
          :disabled="!modal.formIsValid || !modal.inputs.selectedValueSet.length || loading.postValueSet"
          @click="save"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { cloneDeep, debounce } from 'lodash'
import ValuesetService from '@/services/ValuesetService'

const initialModal = {
  fetchedValueSets: [],

  formIsValid: true,
  inputs: {
    selectedValueSet: []
  },
  search: '',
  lastSearch: '',
  projectFilters: []
}
export default {
  name: 'ValueSetAddReference',
  props: {
    // Open/close state of the modal
    value: {
      type: Boolean,
      required: false,
      default: false
    },
    // Select the valueset after creation
    autoSelect: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      modal: cloneDeep(initialModal),
      loading: {
        getValueSets: false,
        postValueSet: false
      }
    }
  },
  computed: {
    filteredFetchedValueSets () {
      const list = this.modal.fetchedValueSets
        .filter(tm => tm.ident && tm.ident !== this.projectInFocus?.prefix)
      if (this.hasActiveProjectFilters) {
        return list
          .filter(tm => this.modal.projectFilters
            .find(filter => [tm.ident, tm.project].includes(filter.name) && filter.active))
      }
      return list
    },
    hasActiveProjectFilters () {
      return !!this.modal.projectFilters.find(f => f.active)
    },
    headers () {
      return [
        { value: 'id', text: this.$t('id'), sortable: false },
        { value: 'displayName', text: this.$t('display-name'), sortable: true },
        { value: 'effectiveDate', text: this.$t('version'), sortable: true },
        { value: 'project', text: this.$t('prefix'), sortable: true },
        { value: 'projectName', text: this.$t('project-name'), sortable: true }
      ]
    }
  },
  methods: {
    closeModal () {
      this.modal = cloneDeep(initialModal)
      this.$emit('input', false)
    },
    async fetchValueSets () {
      try {
        const search = this.modal.search
        if (!search.length || search === this.modal.lastSearch) {
          return
        }
        this.modal.lastSearch = search

        this.loading.getValueSets = true
        const params = {
          prefix: this.projectInFocus?.prefix,
          search: this.modal.search,
          includebbr: true,
          resolve: true
        }
        const list = await ValuesetService.getValueset(params).then(res => res.data?.valueSet) || []
        this.modal.fetchedValueSets = list

        // Set projectFilters without duplicates
        const projects = [...new Map(this.modal.fetchedValueSets.map(obj => [obj.prefix || obj.ident, obj])).values()]
        this.modal.projectFilters = projects.map(obj => ({ name: obj.prefix || obj.ident, active: false }))
          .filter(obj => obj.name !== undefined)
          .filter(obj => obj.name !== this.projectInFocus?.prefix)
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || error.message || 'Error'
        this.$oops(errorMessage)
      }
      this.loading.getValueSets = false
    },

    performDebouncedSearch: debounce(function () {
      if (this.modal.search?.length >= 3) {
        this.fetchValueSets()
      }
    }, 500),

    async save () {
      try {
        this.loading.postValueSet = true
        const params = {
          sourceId: this.modal.inputs.selectedValueSet[0]?.id,
          sourceEffectiveDate: this.modal.inputs.selectedValueSet[0]?.effectiveDate,
          refOnly: true,
          prefix: this.projectInFocus?.prefix
        }
        this.$store.commit('terminology/preserveValueSetItemOnSelect', true)
        const setItem = this.autoSelect
        await this.$store.dispatch('terminology/cloneValueSet', { params, setItem, valueSetForList: this.modal.inputs.selectedValueSet[0] })
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || error.message || 'Error'
        this.$oops(errorMessage)
      }
      this.$store.commit('terminology/preserveValueSetItemOnSelect', false)
      this.loading.postValueSet = false
      this.closeModal()
    },
    selectItem (item, { select, isSelected }) {
      select(!isSelected)
    },
    setSearch (search) {
      this.modal.search = search
    }
  },
  watch: {
    async value (isOpen) {
      if (isOpen) {
        try {
          await this.$nextTick()
          this.$refs.searchInput.focus()
        } catch (error) {
          console.error(error)
        }
      }
    },
    'modal.search' () {
      this.performDebouncedSearch()
    }
  }

}
</script>

<style lang="scss" scoped>
::v-deep .v-data-table tbody tr {
  cursor: pointer;
}
</style>
